import React, { useState, useEffect } from 'react';
import { Modal, Button, Card, Row, Col } from 'react-bootstrap';

import { CreateUpdateSurveyEmailContentDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useTranslation } from '../../../translations';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { Waiting } from '../../../common/components/Waiting/Waiting';

interface EmailPreviewModalProps {
  show: boolean;
  onHide: () => void;
  emailData: CreateUpdateSurveyEmailContentDto;
  placeholders?: {
    tag: string;
    sampleData: string;
    sampleDataEnglish: string;
  }[];
}

export const EmailPreviewModal: React.FC<EmailPreviewModalProps> = props => {
  const { t, tk } = useTranslation('administrationLng');
  const pok = usePokCore();
  const [previewPl, setPreviewPl] = useState<string>('');
  const [previewEn, setPreviewEn] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchPreviews = async () => {
      if (!props.show) {
        return;
      }
      setIsLoading(true);
      try {
        const result = await pok.surveys.previewEmail({
          emailPl: props.emailData.emailBody,
          emailEn: props.emailData.emailBodyEn,
        });
        setPreviewPl(result.emailPl || '');
        setPreviewEn(result.emailEn || '');
      } catch (error) {
        console.error('Error fetching email previews:', error);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchPreviews();
  }, [props.show, props.emailData.emailBody, props.emailData.emailBodyEn, pok]);

  return (
    <Modal
      show={props.show}
      onHide={props.onHide}
      centered
      dialogClassName="modal-90w"
      backdrop="static"
    >
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="d-flex align-items-center gap-2">
          {t(tk.survey.emailTemplatePreview)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {isLoading ? (
          <Waiting />
        ) : (
          <Row className="g-4">
            <Col md={6}>
              <Card>
                <Card.Header>
                  <span>{t(tk.survey.polishVersion)}</span>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">
                      {t(tk.survey.subject)}:
                    </div>
                    <div className="fw-semibold text-dark p-2 bg-light rounded">
                      {props.emailData.emailSubject}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted small mb-1">
                      {t(tk.survey.emailBody)}:
                    </div>
                    <div
                      className="email-preview-content p-3 bg-white border rounded"
                      dangerouslySetInnerHTML={{
                        __html: previewPl,
                      }}
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card>
                <Card.Header>
                  <span>{t(tk.survey.englishVersion)}</span>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">
                      {t(tk.survey.subject)}:
                    </div>
                    <div className="fw-semibold text-dark p-2 bg-light rounded">
                      {props.emailData.emailSubjectEn}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted small mb-1">
                      {t(tk.survey.emailBody)}:
                    </div>
                    <div
                      className="email-preview-content p-3 bg-white border rounded"
                      dangerouslySetInnerHTML={{
                        __html: previewEn,
                      }}
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-light">
        <Button variant="secondary" onClick={props.onHide}>
          {t(tk.button.close)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
